import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    FaTachometerAlt, FaUser, FaNewspaper, FaCogs, FaSignOutAlt,
    FaBars, FaTimes, FaBuilding, FaBell, FaEnvelope, FaChevronDown,
    FaShieldAlt, FaList, FaHome, FaFileAlt, FaUsers, FaChartBar
} from 'react-icons/fa';

// Pemetaan ikon dari string ke komponen
const iconMap = {
    dashboard: FaTachometerAlt,
    users: FaUsers,
    shield: FaShieldAlt,
    menu: FaList,
    profil: FaUser,
    artikel: FaNewspaper,
    layanan: FaCogs,
    home: FaHome,
    file: FaFileAlt,
    chart: FaChartBar,
};

const AdminLayout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [profileDropdown, setProfileDropdown] = useState(false);
    const { user, menus, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    // Fallback menu statis jika menus dari API kosong/undefined
    const fallbackMenuItems = [
        {
            id: 'dashboard',
            name: 'Dashboard',
            icon: 'dashboard',
            route: '/admin',
            children: []
        },
        {
            id: 'profil',
            name: 'Kelola Profil',
            icon: 'profil',
            route: '/admin/profil',
            children: [
                { id: 'visi-misi', name: 'Visi & Misi', route: '/admin/profil/visi-misi', icon: '' },
                { id: 'tupoksi', name: 'Tupoksi', route: '/admin/profil/tupoksi', icon: '' },
                { id: 'struktur', name: 'Struktur Organisasi', route: '/admin/profil/struktur', icon: '' },
                { id: 'standar-pelayanan', name: 'Standar Pelayanan', route: '/admin/profil/standar-pelayanan', icon: '' },
                { id: 'kontak', name: 'Kontak', route: '/admin/profil/kontak', icon: '' }
            ]
        },
        {
            id: 'artikel',
            name: 'Artikel & Berita',
            icon: 'artikel',
            route: '/admin/artikel',
            children: [
                { id: 'semua-artikel', name: 'Semua Artikel', route: '/admin/artikel', icon: '' },
                { id: 'tambah-artikel', name: 'Tambah Artikel', route: '/admin/artikel/tambah', icon: '' },
                { id: 'kategori', name: 'Kategori', route: '/admin/artikel/kategori', icon: '' }
            ]
        },
        {
            id: 'layanan',
            name: 'Layanan',
            icon: 'layanan',
            route: '/admin/layanan',
            children: [
                { id: 'semua-layanan', name: 'Semua Layanan', route: '/admin/layanan', icon: '' },
                { id: 'tambah-layanan', name: 'Tambah Layanan', route: '/admin/layanan/tambah', icon: '' }
            ]
        }
    ];

    const menuItems = menus && menus.length > 0 ? menus : fallbackMenuItems;

    const getIconComponent = (iconString) => {
        if (!iconString) return FaList;
        return iconMap[iconString.toLowerCase()] || FaList;
    };

    const resolvePath = (item) => {
        if (item.route && item.route !== '#') return item.route;
        return item.children && item.children.length > 0 ? item.children[0].route : '/admin';
    };

    const handleLogout = async () => {
        await logout();
        navigate('/admin/login');
    };

    const isActive = (path) => {
        if (path === '/admin' && location.pathname === '/admin') return true;
        if (path !== '/admin' && location.pathname.startsWith(path)) return true;
        return false;
    };

    // Render menu rekursif — font lebih kecil & spacing lebih rapi
    const renderMenuItems = (items, level = 0) => {
        return items.map((item, index) => {
            const IconComp = getIconComponent(item.icon);
            const hasChildren = item.children && item.children.length > 0;
            const itemPath = resolvePath(item);
            const active = isActive(itemPath);

            return (
                <div key={item.id || index}>
                    {hasChildren ? (
                        <div>
                            <Link
                                to={itemPath}
                                className={`flex items-center px-3 py-2 rounded-md transition-all duration-200 group text-[13px] ${active
                                    ? 'bg-[#0D9488] text-white shadow-sm'
                                    : 'text-gray-300 hover:bg-white/10'
                                    }`}
                            >
                                <IconComp className={`text-base flex-shrink-0 ${!sidebarOpen && 'mx-auto'}`} />
                                {sidebarOpen && (
                                    <>
                                        <span className="ml-3 flex-1 font-medium truncate">{item.name}</span>
                                        <FaChevronDown
                                            className={`text-[10px] transition-transform ${active ? 'rotate-180' : ''
                                                }`}
                                        />
                                    </>
                                )}
                            </Link>
                            {sidebarOpen && active && (
                                <div className="ml-4 mt-1 space-y-0.5 border-l border-white/10 pl-2">
                                    {renderMenuItems(item.children, level + 1)}
                                </div>
                            )}
                        </div>
                    ) : (
                        <Link
                            to={itemPath}
                            className={`flex items-center px-3 py-2 rounded-md transition-all duration-200 text-[13px] ${active
                                ? 'bg-[#0D9488] text-white shadow-sm'
                                : 'text-gray-300 hover:bg-white/10'
                                }`}
                        >
                            <IconComp className={`text-base flex-shrink-0 ${!sidebarOpen && 'mx-auto'}`} />
                            {sidebarOpen && <span className="ml-3 font-medium truncate">{item.name}</span>}
                        </Link>
                    )}
                </div>
            );
        });
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] flex">
            <a href="#admin-main-content" className="skip-to-content">
                Lewati ke konten utama
            </a>

            {/* Sidebar */}
            <div
                className={`fixed inset-y-0 left-0 z-50 bg-gradient-to-b from-[#1E3A8A] to-[#0F172A] text-white transition-all duration-300 flex flex-col ${sidebarOpen ? 'w-64' : 'w-20'
                    }`}
            >
                {/* Logo */}
                <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
                    <div className={`flex items-center ${!sidebarOpen && 'justify-center w-full'}`}>
                        <FaBuilding className="text-xl text-[#0D9488] flex-shrink-0" />
                        {sidebarOpen && (
                            <div className="ml-3 min-w-0">
                                <h1 className="font-bold text-sm truncate">BPKAD Admin</h1>
                                <p className="text-[11px] text-gray-400 truncate">Kota Magelang</p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="text-white/60 hover:text-white flex-shrink-0"
                    >
                        {sidebarOpen ? <FaTimes /> : <FaBars />}
                    </button>
                </div>

                {/* Menu — SCROLLABLE */}
                <nav
                    className="flex-1 mt-3 px-2 pb-4 space-y-0.5 overflow-y-auto overflow-x-hidden
                        scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent"
                >
                    {renderMenuItems(menuItems)}
                </nav>

                {/* Logout di bawah (opsional, tetap terlihat) */}
                <div className="border-t border-white/10 p-2 flex-shrink-0">
                    <button
                        onClick={handleLogout}
                        className={`flex items-center w-full px-3 py-2 rounded-md text-[13px] text-red-300 hover:bg-red-500/20 transition-colors ${!sidebarOpen && 'justify-center'
                            }`}
                    >
                        <FaSignOutAlt className="text-base flex-shrink-0" />
                        {sidebarOpen && <span className="ml-3 font-medium">Keluar</span>}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
                {/* Top Navigation */}
                <header className="bg-white shadow-sm sticky top-0 z-40">
                    <div className="flex items-center justify-between px-6 py-3">
                        <div>
                            <h2 className="text-lg font-bold text-[#0F172A]">
                                {menuItems.find(m => isActive(resolvePath(m)))?.name || 'Dashboard'}
                            </h2>
                        </div>

                        <div className="flex items-center space-x-4">
                            {/*<button className="relative text-gray-500 hover:text-[#0D9488] transition-colors">
                                <FaBell className="text-lg" />
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                                    3
                                </span>
                            </button>

                            <button className="relative text-gray-500 hover:text-[#0D9488] transition-colors">
                                <FaEnvelope className="text-lg" />
                                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                                    5
                                </span>
                            </button>*/}

                            <div className="relative">
                                <button
                                    onClick={() => setProfileDropdown(!profileDropdown)}
                                    className="flex items-center space-x-3 hover:bg-gray-50 rounded-lg px-3 py-1.5 transition-colors"
                                >
                                    <div className="w-8 h-8 bg-gradient-to-br from-[#1E3A8A] to-[#0D9488] rounded-full flex items-center justify-center text-white font-bold text-sm">
                                        {user?.name?.charAt(0) || 'A'}
                                    </div>
                                    <div className="text-left hidden md:block">
                                        <p className="text-sm font-semibold text-gray-700">{user?.name}</p>
                                        <p className="text-xs text-gray-500 capitalize">
                                            {user?.roles?.length ? user.roles.join(', ') : 'Admin'}
                                        </p>
                                    </div>
                                </button>

                                {profileDropdown && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border py-1 z-50">
                                        <Link to="/admin/profil" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                            👤 Profil Saya
                                        </Link>
                                        <Link to="/admin/pengaturan" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                                            ⚙️ Pengaturan
                                        </Link>
                                        <hr className="my-1" />
                                        <button
                                            onClick={handleLogout}
                                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                        >
                                            <FaSignOutAlt className="inline mr-2" />
                                            Keluar
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                <main id="admin-main-content" className="p-6">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;